import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatMinutes, formatDecimalHours, formatWeekLabel } from "@/lib/timeFormat";
import ExportCsvButton from "@/components/ExportCsvButton";
import StatusPill from "@/components/hours/StatusPill";
import StatusFilter from "@/components/hours/StatusFilter";

// Legacy support: revised (used by resubmit flow) surfaces as submitted.

export default function ApprovalsHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from: "", to: "", status: "all" });
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.from) p.set("from", filters.from);
      if (filters.to) p.set("to", filters.to);
      if (filters.status && filters.status !== "all") p.set("status", filters.status);
      const { data } = await api.get(`/approvals/history?${p.toString()}`);
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const revoke = async (id) => {
    setBusy(id);
    try {
      await api.post(`/approvals/timesheets/${id}/revoke`);
      toast.success("Approval revoked. Worker notified.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-history-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Approvals</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">History</h1>
        <p className="mt-2 text-muted-foreground">Past decisions. You can undo approvals during your workspace's revoke window.</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">From (Sat)</Label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from" />
          </div>
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">To (Sat)</Label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
            <StatusFilter
              value={filters.status === "all" ? null : filters.status}
              onChange={(v) => setFilters({ ...filters, status: v || "all" })}
              persistKey="hours-filter:approvals-history"
            />
          </div>
          <Button onClick={load} data-testid="filter-apply">Apply</Button>
          <ExportCsvButton
            endpoint="tenant/timesheets/export.csv"
            filename={`quanta-timesheets-${filters.status || "all"}.csv`}
            filters={{
              status: filters.status && filters.status !== "all" ? filters.status : "approved",
              period_start: filters.from || undefined,
              period_end: filters.to || undefined,
            }}
            testId="export-csv-history"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Decisions ({items.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Worker</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Decision at</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="py-8 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No decisions yet.</TableCell></TableRow>}
              {items.map((it) => {
                const at = it.approved_at || it.rejected_at || null;
                return (
                  <TableRow key={it.id} data-testid={`history-row-${it.id}`}>
                    <TableCell className="font-medium">
                      <Link to={`/app/approvals/${it.id}`} className="hover:underline">{it.worker_name}</Link>
                    </TableCell>
                    <TableCell>{formatWeekLabel(it.period_start_local, it.period_end_local)}</TableCell>
                    <TableCell className="tabular-nums">{formatMinutes(it.total_minutes)} ({formatDecimalHours(it.total_minutes)}h)</TableCell>
                    <TableCell>
                      <StatusPill
                        status={it.status === "revised" ? "submitted" : it.status}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{at ? new Date(at).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      {it.status === "approved" && (
                        <Button size="sm" variant="ghost" onClick={() => revoke(it.id)} disabled={busy === it.id} data-testid={`revoke-${it.id}`}>
                          {busy === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
